import React, { useEffect, useState } from "react";

import {
  CourseCardVisualBody,
  CourseCardVisualLayout,
  CourseCoverVisual,
} from "../../components/CourseCover/CourseCover";
import { useNotify } from "../../context/NotificationContext";
import { fetchStudentCourses } from "../../services/studentPortalService/studentPortalService";
import type { StudentCourse } from "../../services/studentPortalService/studentPortalService.types";
import { logger } from "../../utils/logger";
import StudentBreadcrumbs from "./StudentBreadcrumbs";
import { CoursesSectionHeader } from "./StudentCourseListHeader.styles";
import {
  CourseChevron,
  CourseDescription,
  CourseFoot,
  CourseFootStrong,
  CourseFootText,
  CourseList,
  CourseMain,
  CourseMeta,
  CourseChip,
  CourseSide,
  CourseTitle,
  CourseTop,
  CoursesPageLead,
  CoursesPageShell,
  CoursesPageTitle,
  DemoLoadingState,
  EmptyCourses,
  EmptyCoursesAction,
  EmptyCoursesText,
  EmptyCoursesTitle,
  HomeworkProgress,
  HomeworkTrack,
  MyCourseCard,
  PrimaryCourseLink,
} from "./studentCourses.styles";

const StudentCoursesPage: React.FC = () => {
  const [courses, setCourses] = useState<StudentCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useNotify();

  useEffect(() => {
    let active = true;
    fetchStudentCourses()
      .then((data) => active && setCourses(data))
      .catch((error) => {
        logger.error("Ошибка загрузки курсов ученика:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось загрузить курсы", life: 3000 });
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [toast]);

  return (
    <CoursesPageShell>
      <CoursesSectionHeader>
        <StudentBreadcrumbs items={[{ label: "Мои курсы" }]} />
        <CoursesPageTitle>Мои курсы</CoursesPageTitle>
        <CoursesPageLead>Курсы, к которым преподаватели открыли вам доступ.</CoursesPageLead>
      </CoursesSectionHeader>

      {loading ? (
        <DemoLoadingState>Загружаем курсы…</DemoLoadingState>
      ) : courses.length === 0 ? (
        <EmptyCourses>
          <EmptyCoursesTitle>Доступных курсов пока нет</EmptyCoursesTitle>
          <EmptyCoursesText>
            Найдите курс в каталоге и подайте заявку. Как только преподаватель её одобрит, курс появится здесь.
          </EmptyCoursesText>
          <EmptyCoursesAction>
            <PrimaryCourseLink to="/student/catalog">Открыть каталог</PrimaryCourseLink>
          </EmptyCoursesAction>
        </EmptyCourses>
      ) : (
        <CourseList>
          {courses.map((course) => {
            const percent = course.progress.percent;
            return (
              <MyCourseCard key={course.id} to={`/student/courses/${course.id}`}>
                <CourseCardVisualLayout>
                  <CourseCoverVisual src={course.cover} alt="" variant="card" />
                  <CourseCardVisualBody>
                    <CourseTop>
                      <CourseMain>
                        <CourseMeta>{course.teacher.organization_name} · {course.category}</CourseMeta>
                        <CourseTitle>{course.title}</CourseTitle>
                        <CourseDescription>{course.description}</CourseDescription>
                      </CourseMain>
                      {percent === 100 && (
                        <CourseSide>
                          <CourseChip $done>Все задания проверены</CourseChip>
                        </CourseSide>
                      )}
                    </CourseTop>

                    <CourseFoot>
                      {percent === null ? (
                        <CourseFootText>В курсе пока нет домашних заданий</CourseFootText>
                      ) : (
                        <>
                          <HomeworkTrack aria-label={`Прогресс домашних заданий ${percent}%`}>
                            <HomeworkProgress $percent={percent} />
                          </HomeworkTrack>
                          <CourseFootText>
                            Домашние задания: проверено{" "}
                            <CourseFootStrong>{course.progress.rated} из {course.progress.total}</CourseFootStrong>
                          </CourseFootText>
                        </>
                      )}
                      <CourseChevron aria-hidden="true" />
                    </CourseFoot>
                  </CourseCardVisualBody>
                </CourseCardVisualLayout>
              </MyCourseCard>
            );
          })}
        </CourseList>
      )}
    </CoursesPageShell>
  );
};

export default StudentCoursesPage;
